import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { Store } from '@reduxjs/toolkit';
import type { RootState } from '@store/store';
import { getApiBaseUrl } from '@api/baseUrl';
import { logout } from '@store/slices/adminAuthSlice';

let injectedStore: Store | null = null;

export const injectStore = (store: Store) => {
  injectedStore = store;
};

export const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
});

type AppAxiosRequestConfig = AxiosRequestConfig & {
  skipErrorToast?: boolean;
  skipAuthLogout?: boolean;
};

const normalizeLocale = (value: unknown): string => {
  if (typeof value !== 'string') {
    return 'en';
  }

  const normalized = value.trim().toLowerCase().replace('_', '-');
  if (!normalized) {
    return 'en';
  }

  const [base] = normalized.split('-');
  return base || 'en';
};

const resolveLocale = () => {
  if (typeof document !== 'undefined' && document.documentElement.lang) {
    return normalizeLocale(document.documentElement.lang);
  }

  if (typeof navigator !== 'undefined') {
    const preferredLocale = navigator.language || navigator.languages?.[0];
    return normalizeLocale(preferredLocale);
  }

  return 'en';
};

const getErrorMessage = (error: AxiosError) => {
  const data = error.response?.data as { message?: string; error?: string } | string | undefined;

  if (typeof data === 'string') {
    return data;
  }

  if (data && typeof data === 'object') {
    return data.message || data.error || error.message;
  }

  return error.message || 'Request failed';
};

axiosInstance.interceptors.request.use(config => {
  const locale = resolveLocale();
  const headers = AxiosHeaders.from(config.headers);

  if (!headers.has('x-locale')) {
    headers.set('x-locale', locale);
  }

  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', locale);
  }

  config.headers = headers;
  return config;
});

axiosInstance.interceptors.response.use(
  response => response,
  error => {
    const requestConfig = error?.config as (InternalAxiosRequestConfig & AppAxiosRequestConfig) | undefined;
    if (error?.response?.status === 401) {
      const currentToken = injectedStore
        ? (injectedStore.getState() as RootState).adminAuth.token
        : null;
      const requestAuthorization = AxiosHeaders.from(requestConfig?.headers).get('Authorization');
      // A response belongs to the token it was sent with. Optional metrics and
      // late responses from an earlier session cannot invalidate new credentials.
      if (injectedStore && !requestConfig?.skipAuthLogout && currentToken && requestAuthorization === `Bearer ${currentToken}`) {
        injectedStore.dispatch(logout());
      }
      return Promise.reject(error);
    }

    if (error?.code !== 'ERR_CANCELED' && !requestConfig?.skipErrorToast) {
      message.error(getErrorMessage(error));
    }

    return Promise.reject(error);
  }
);

export const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      body?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      responseType?: AxiosRequestConfig['responseType'];
      headers?: AxiosRequestConfig['headers'];
      suppressErrorToast?: boolean;
      skipAuthLogout?: boolean;
    },
    unknown,
    unknown
  > =>
  async (
    { url, method = 'GET', data, body, params, headers, responseType, suppressErrorToast, skipAuthLogout },
    { getState }
  ) => {
    try {
      const state = getState() as RootState;
      const token = state.adminAuth.token;
      const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

      const result = await axiosInstance({
        url,
        method,
        data: typeof body !== 'undefined' ? body : data,
        params,
        responseType,
        headers: { ...authHeader, ...(headers || {}) },
        skipErrorToast: suppressErrorToast !== false,
        skipAuthLogout,
      } as AppAxiosRequestConfig);

      return { data: result.data };
    } catch (err) {
      const error = err as AxiosError;
      return {
        error: {
          status: error.response?.status,
          data: error.response?.data || error.message,
        },
      };
    }
  };
